import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    useEffect,
} from 'react';
import PropTypes from 'prop-types';
import LytModal from './LytModal';
import { EMPTY_OBJECT } from '../../../constants/stable-defaults';

/**
 * ModalContext provides a global, promise-based API for opening a single modal at a time.
 * Usage:
 *   const { openModal } = useModal();
 *   const result = await openModal('confirm', { title: 'Delete item?' });
 */
const ModalContext = createContext(null);
export const useModal = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModal must be used within a ModalProvider');
    }
    return context;
};

/**
 * ModalProvider
 *
 * Props:
 * - registry: object map of { [modalId]: ReactComponent }
 * - children: app tree
 * - allowQueue: optional boolean - if true, queue subsequent open requests (default: false → reject)
 */
export const ModalProvider = ({
    registry = EMPTY_OBJECT,
    children,
    allowQueue = false,
}) => {
    const [activeModal, setActiveModal] = useState(null);
    const queueRef = useRef([]);
    const previousFocusedElementRef = useRef(null);
    const lockScroll = useCallback(() => {
        if (typeof document !== 'undefined') {
            document.body.style.overflow = 'hidden';
        }
    }, []);
    const unlockScroll = useCallback(() => {
        if (typeof document !== 'undefined') {
            document.body.style.overflow = '';
        }
    }, []);
    const restoreFocus = useCallback(() => {
        // Use setTimeout to avoid React rendering conflicts
        setTimeout(() => {
            try {
                const el = previousFocusedElementRef.current;
                if (
                    el &&
                    typeof el.focus === 'function' &&
                    document.contains(el)
                ) {
                    el.focus();
                }
            } catch (_) {
                // noop
            }
        }, 0);
    }, []);
    const closeActiveModal = useCallback(
        (result) => {
            setActiveModal((current) => {
                if (!current) return null;
                try {
                    if (current.resolver) current.resolver(result);
                } finally {
                    unlockScroll();
                    restoreFocus();
                }
                return null;
            });
        },
        [restoreFocus, unlockScroll]
    );
    const rejectActiveModal = useCallback(
        (error) => {
            setActiveModal((current) => {
                if (!current) return null;
                try {
                    if (current.rejecter) current.rejecter(error);
                } finally {
                    unlockScroll();
                    restoreFocus();
                }
                return null;
            });
        },
        [restoreFocus, unlockScroll]
    );
    const processNextInQueue = useCallback(() => {
        if (!allowQueue) return;
        const next = queueRef.current.shift();
        if (!next) return;
        // Call openModal with the queued request
        next();
    }, [allowQueue]);
    useEffect(() => {
        if (!activeModal) {
            processNextInQueue();
        }
    }, [activeModal, processNextInQueue]);
    const openModal = useCallback(
        (id, props = {}) => {
            const open = () =>
                new Promise((resolve, reject) => {
                    if (activeModal) {
                        // Should not happen when queued, but guard anyway
                        reject(new Error('A modal is already open'));
                        return;
                    }

                    // Check if modal component exists before proceeding
                    const Component = registry?.[id];
                    if (!Component) {
                        console.warn(
                            `Modal component "${id}" not found in registry`
                        );
                        reject(new Error(`Modal component "${id}" not found`));
                        return;
                    }
                    try {
                        previousFocusedElementRef.current =
                            typeof document !== 'undefined'
                                ? document.activeElement
                                : null;
                    } catch (_) {
                        previousFocusedElementRef.current = null;
                    }
                    lockScroll();
                    setActiveModal({
                        id,
                        props,
                        resolver: resolve,
                        rejecter: reject,
                    });
                });

            // If a modal is already open
            if (activeModal) {
                if (allowQueue) {
                    return new Promise((resolve, reject) => {
                        queueRef.current.push(() => {
                            open().then(resolve).catch(reject);
                        });
                    });
                }
                return Promise.reject(new Error('A modal is already open'));
            }
            return open();
        },
        [activeModal, allowQueue, lockScroll, registry]
    );
    const closeModal = useCallback(
        (result) => {
            closeActiveModal(result);
        },
        [closeActiveModal]
    );
    const value = useMemo(
        () => ({
            openModal,
            closeModal,
            isOpen: !!activeModal,
        }),
        [openModal, closeModal, activeModal]
    );
    const Component = activeModal ? registry?.[activeModal.id] : null;
    return (
        <ModalContext.Provider value={value}>
            {children}
            {activeModal && Component && (
                <Component
                    {...activeModal.props}
                    open={true}
                    onClose={(result) =>
                        closeActiveModal(
                            result || {
                                dismissed: true,
                            }
                        )
                    }
                    closeModal={(result) => closeActiveModal(result)}
                    cancelModal={(reason) =>
                        rejectActiveModal(
                            reason || new Error('Modal cancelled')
                        )
                    }
                />
            )}
        </ModalContext.Provider>
    );
};
ModalProvider.propTypes = {
    registry: PropTypes.object,
    // { [id]: Component }
    children: PropTypes.node.isRequired,
    allowQueue: PropTypes.bool,
};
export default ModalContext;
